'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { appointmentSchema, type AppointmentFormData } from '@/lib/schemas'
import { useState } from 'react'
import { siteConfig } from '@/config/site'

export function AppointmentForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
  })

  const onSubmit = async (data: AppointmentFormData) => {
    setIsSubmitting(true)
    setSubmitMessage('')
    try {
      // Send appointment via email API
      const response = await fetch('/api/book-appointment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to book appointment')
      }

      setSubmitMessage('✅ Appointment request submitted successfully! We will contact you shortly via email.')
      reset()

      // Optional: Also create WhatsApp message for backup contact
      // const message = `Hello, I'd like to book an appointment.\n\nName: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\nPreferred Date: ${data.date}\nPreferred Time: ${data.time}\nService: ${data.service}\n${data.message ? `Message: ${data.message}` : ''}`
      // const encodedMessage = encodeURIComponent(message)
      // const whatsappPhone = siteConfig.whatsapp.replace(/[^\d+]/g, '')
      // window.open(`https://wa.me/${whatsappPhone}?text=${encodedMessage}`)
    } catch (error) {
      console.error('Appointment booking error:', error)
      setSubmitMessage('❌ Error submitting appointment. Please try again or contact us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            {...register('name')}
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
            placeholder="Your name"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            {...register('email')}
            type="email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
            placeholder="your@email.com"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            {...register('phone')}
            type="tel"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
            placeholder="+1 (555) 123-4567"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
          <select
            {...register('service')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
          >
            <option value="">Select a service</option>
            <option value="medical-consultation">Medical Consultation</option>
            <option value="mental-assessment">Mental Assessment</option>
            <option value="iv-vitamin-therapy">IV Vitamin Therapy</option>
            <option value="counselling">Counselling</option>
            <option value="life-coaching">Life Coaching</option>
            <option value="speaking-engagement">Speaking Engagement</option>
            <option value="radio-tv-interview">Radio/TV Interview</option>
          </select>
          {errors.service && <p className="text-red-500 text-sm mt-1">{errors.service.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Date</label>
          <input
            {...register('date')}
            type="date"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
          />
          {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Time</label>
          <input
            {...register('time')}
            type="time"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
          />
          {errors.time && <p className="text-red-500 text-sm mt-1">{errors.time.message}</p>}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Message</label>
        <textarea
          {...register('message')}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-primary focus:border-transparent outline-none transition"
          placeholder="Any additional information..."
          rows={4}
        />
      </div>

      {submitMessage && (
        <div
          className={`p-4 rounded-lg text-sm ${
            submitMessage.includes('Error')
              ? 'bg-red-100 text-red-700'
              : 'bg-green-100 text-green-700'
          }`}
        >
          {submitMessage}
        </div>
      )}

      <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
        {isSubmitting ? 'Submitting...' : 'Book a Consultation'}
      </button>

      <p className="text-sm text-gray-600 text-center">
        Or contact us directly via{' '}
        <a
          href={`https://wa.me/${siteConfig.whatsapp.replace(/[^\d+]/g, '')}`}
          className="text-medical-primary hover:text-medical-secondary font-medium"
        >
          WhatsApp
        </a>
      </p>
    </form>
  )
}
